import { Component } from 'react';
import { PACKAGES } from './constants';
import PACKAGE_FEATURES, { PackageFeatures } from './package-features';
import { ComparisonPackageFeatures } from './ComparisonTableTypes';
import SummaryComparisonPackageFeatures from './definitions/SummaryComparisonPackageFeatures';

import {
  EnterprisePackageHeader,
  TeamPackageHeader,
  MonthlyPackageHeader,
  AnnualPackageHeader,
} from '~pricing/components/Summary';
import PackageCta from '~pricing/components/PackageCta';
import EnterprisePackageCta from '~pricing/components/EnterprisePackageCta';

/**
 * Package config that defines what is displayed in the upper summary section
 */
export interface PackageSummary {
  // The list of features to render in the package summary display
  features: PackageFeatures;

  // The component to render the package summary display header
  headerRenderer: Component;

  // The component to render the package summary display footer
  footerRenderer: Component;

  // If the package should be visually highlighted
  highlighted?: boolean;
}

/**
 * List of features that a package contains for the comparison table display
 */
export interface PackageComparison {
  // The text displayed underneath the package title in the comparison table header
  byline: string;

  // Array of features that this package includes
  features: ComparisonPackageFeatures;

  // The renderer for the footer CTA
  ctaRenderer: Component;
}

export interface SummaryPackageConfig {
  summary: PackageSummary;
  comparison?: PackageComparison;
}

type PackageList = {
  [packageId: string]: SummaryPackageConfig;
};

/**
 * This file contains default rendering configuration for each package that's displayed
 * on the pricing summary page. Each package summary config includes:
 * - `features`: An array of features to render in the package summary
 * - `headerRenderer`: The component to use for the package header.
 *                     This usually displays price and top CTA.
 * - `footerRenderer`: The component to use for the package footer.
 *                     This usually contains a CTA and any footnotes for the package
 *
 * Experiments can override these for custom rendering.
 * TODO (mhackmann): Actually allow experiments to override anything
 */

const SUMMARY_PAGE_RENDERERS: PackageList = Object.freeze({
  // Personal / Individual Packages
  [PACKAGES.StandardMonthly]: {
    summary: {
      features: PACKAGE_FEATURES[PACKAGES.StandardMonthly],
      headerRenderer: MonthlyPackageHeader,
      footerRenderer: PackageCta,
    },
  },
  [PACKAGES.AdvantageAnnual]: {
    summary: {
      features: PACKAGE_FEATURES[PACKAGES.AdvantageAnnual],
      headerRenderer: AnnualPackageHeader,
      footerRenderer: PackageCta,
      highlighted: true,
    },
  },
  [PACKAGES.PremierAnnual]: {
    summary: {
      features: PACKAGE_FEATURES[PACKAGES.PremierAnnual],
      headerRenderer: AnnualPackageHeader,
      footerRenderer: PackageCta,
    },
  },

  // Business / Teams Packages
  [PACKAGES.TeamAdvantage]: {
    summary: {
      features: PACKAGE_FEATURES[PACKAGES.TeamAdvantage],
      headerRenderer: TeamPackageHeader,
      footerRenderer: PackageCta,
    },
    comparison: {
      byline: 'Starts at 3 users',
      features: SummaryComparisonPackageFeatures[PACKAGES.TeamAdvantage],
      ctaRenderer: PackageCta,
    },
  },
  [PACKAGES.TeamPremier]: {
    summary: {
      features: PACKAGE_FEATURES[PACKAGES.TeamPremier],
      headerRenderer: TeamPackageHeader,
      footerRenderer: PackageCta,
    },
    comparison: {
      byline: 'Starts at 3 users',
      features: SummaryComparisonPackageFeatures[PACKAGES.TeamPremier],
      ctaRenderer: PackageCta,
    },
  },
  [PACKAGES.Enterprise]: {
    summary: {
      features: PACKAGE_FEATURES[PACKAGES.Enterprise],
      headerRenderer: EnterprisePackageHeader,
      footerRenderer: EnterprisePackageCta,
    },
    comparison: {
      byline: 'For large organizations',
      features: SummaryComparisonPackageFeatures[PACKAGES.Enterprise],
      ctaRenderer: EnterprisePackageCta,
    },
  },
});

export default SUMMARY_PAGE_RENDERERS;
